import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, X, Music } from "lucide-react";

const WCFY_COVER = "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/kd42ok0u_FosterCareAnthem_WereComingForYou_2026.png";

const VIDEOS = [
  { id: "3b6DNPr5bJI", label: "Original", order: "01" },
  { id: "hBVCoG4Smfo", label: "R&B Version", order: "02" },
];

const DISMISS_KEY = "wcfy_player_dismissed";

export default function AnthemMiniPlayer() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(VIDEOS[0]);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(DISMISS_KEY);
    if (!dismissed) {
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
    setOpen(false);
  };

  const openPlayer = () => setOpen(true);
  const closePlayer = () => setOpen(false);

  if (!visible) return null;

  return (
    <>
      {/* Floating pill */}
      <div
        className="fixed bottom-20 md:bottom-5 right-5 md:right-[200px] z-[60] max-w-[340px] fade-up"
        data-testid="anthem-miniplayer"
      >
        <div className="border border-an-border bg-an-surface/95 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.6)]">
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="absolute -top-2 -right-2 w-6 h-6 border border-an-border bg-an-bg text-an-muted hover:text-an-ink flex items-center justify-center"
            data-testid="miniplayer-dismiss"
          >
            <X size={12} />
          </button>
          <button
            onClick={openPlayer}
            className="group flex items-center gap-3 p-3 w-full text-left"
            data-testid="miniplayer-open"
          >
            <div className="relative w-16 h-16 border border-an-border overflow-hidden shrink-0">
              <img src={WCFY_COVER} alt="We're Coming For You" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-an-bg/40 flex items-center justify-center group-hover:bg-an-bg/10 transition-colors">
                <div className="w-9 h-9 rounded-full bg-an-gold flex items-center justify-center text-an-bg transition-transform group-hover:scale-110">
                  <Play size={14} fill="currentColor" className="ml-0.5" />
                </div>
              </div>
            </div>
            <div className="min-w-0 pr-3">
              <div className="overline text-an-gold mb-1 flex items-center gap-1">
                <Music size={10} /> Now Featuring
              </div>
              <div className="font-display text-lg leading-tight text-an-ink truncate">
                We're Coming For You
              </div>
              <div className="overline text-an-muted mt-1">2 videos · watch now</div>
            </div>
          </button>
        </div>
      </div>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[70] bg-an-bg/90 backdrop-blur-md flex items-center justify-center p-4 md:p-8 fade-up"
          onClick={closePlayer}
          data-testid="miniplayer-modal"
        >
          <div
            className="bg-an-surface border border-an-border max-w-5xl w-full max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-an-border p-5">
              <div>
                <div className="overline text-an-gold mb-1">Trafficking Awareness · Current Battle</div>
                <div className="font-display text-2xl md:text-3xl leading-tight">We're Coming For You</div>
              </div>
              <button
                onClick={closePlayer}
                aria-label="Close"
                className="w-10 h-10 border border-an-border text-an-muted hover:text-an-ink flex items-center justify-center"
                data-testid="miniplayer-close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-an-border">
              {VIDEOS.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setActive(v)}
                  className={`flex-1 p-4 text-left border-r last:border-r-0 border-an-border transition-colors ${
                    active.id === v.id ? "bg-an-bg text-an-ink" : "text-an-muted hover:text-an-ink hover:bg-an-bg/50"
                  }`}
                  data-testid={`miniplayer-tab-${v.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="font-mono text-xs mb-1">/{v.order}</div>
                  <div className="font-display text-lg">{v.label}</div>
                </button>
              ))}
            </div>

            {/* Player */}
            <div className="aspect-video bg-an-bg">
              <iframe
                key={active.id}
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${active.id}?autoplay=1&rel=0`}
                title={`We're Coming For You — ${active.label}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="miniplayer-iframe"
              />
            </div>

            <div className="p-5 border-t border-an-border flex items-center justify-between flex-wrap gap-4">
              <div className="text-sm text-an-muted max-w-xl">
                A direct address to children trapped in human trafficking — and a vow to those fighting to bring them home.
              </div>
              <Link
                to="/anthem/were-coming-for-you"
                onClick={closePlayer}
                className="btn-outline"
                data-testid="miniplayer-view-all"
              >
                View All Editions →
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
